import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { ArtifactRelationship } from '../../../types/artifactRelationship';
import { ArtifactStateTransitionType } from '../../../types/enums';
import './LayeredView.css';

interface LayeredViewProps {
  relationships: ArtifactRelationship[];
  onRelationshipSelect?: (relationshipId: string) => void;
}

export const LayeredView: React.FC<LayeredViewProps> = ({
  relationships,
  onRelationshipSelect,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || relationships.length === 0) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Set up dimensions
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    const margin = { top: 50, right: 50, bottom: 50, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current).attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Group relationships by type
    const relationshipsByType = d3.group(relationships, (d) => d.relationship_type);
    const relationshipTypes = Array.from(
      relationshipsByType.keys()
    ) as ArtifactStateTransitionType[];

    // Create scales
    const xScale = d3
      .scaleBand<ArtifactStateTransitionType>()
      .domain(relationshipTypes)
      .range([0, innerWidth])
      .padding(0.1);

    const yScale = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(relationshipTypes, (type) => relationshipsByType.get(type)?.length || 0) || 1,
      ])
      .range([innerHeight, 0])
      .nice();

    // Create color scale
    const colorScale = d3
      .scaleOrdinal<ArtifactStateTransitionType, string>()
      .domain([
        ArtifactStateTransitionType.OPERATIONAL,
        ArtifactStateTransitionType.RESOURCE,
        ArtifactStateTransitionType.MATERIAL,
        ArtifactStateTransitionType.CREATION,
        ArtifactStateTransitionType.IMPROVEMENT,
      ])
      .range(['#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#9c755f']);

    // Add x-axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    // Add y-axis
    g.append('g').call(d3.axisLeft(yScale).ticks(5));

    // Add bars
    relationshipTypes.forEach((type) => {
      const typeRelationships = relationshipsByType.get(type) || [];
      const barWidth = xScale.bandwidth();
      const barHeight = innerHeight - yScale(typeRelationships.length);

      // Add bar
      g.append('rect')
        .attr('x', xScale(type) || 0)
        .attr('y', yScale(typeRelationships.length))
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', colorScale(type))
        .attr('class', 'relationship-bar')
        .attr('data-relationship-type', type);

      // Add count label
      g.append('text')
        .attr('x', (xScale(type) || 0) + barWidth / 2)
        .attr('y', yScale(typeRelationships.length) - 5)
        .attr('text-anchor', 'middle')
        .attr('class', 'count-label')
        .text(typeRelationships.length);

      // Add relationship type label
      g.append('text')
        .attr('x', (xScale(type) || 0) + barWidth / 2)
        .attr('y', innerHeight + 20)
        .attr('text-anchor', 'middle')
        .attr('class', 'type-label')
        .text(type.replace('_', ' '));
    });

    // Add title
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -20)
      .attr('text-anchor', 'middle')
      .attr('class', 'chart-title')
      .text('Relationships by Type');

    // Add tooltips
    g.selectAll('.relationship-bar')
      .on('mouseover', function (event) {
        const type = (this as Element).getAttribute('data-relationship-type');
        if (type) {
          const typeRelationships =
            relationshipsByType.get(type as ArtifactStateTransitionType) || [];

          // Show tooltip
          const tooltip = d3
            .select('body')
            .append('div')
            .attr('class', 'tooltip')
            .style('position', 'absolute')
            .style('background-color', 'white')
            .style('padding', '5px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '3px')
            .style('pointer-events', 'none')
            .style('opacity', 0);

          tooltip.transition().duration(200).style('opacity', 1);

          // Create relationship list for tooltip
          let relationshipList = '';
          typeRelationships.forEach((rel) => {
            const sourceName = rel.source_artifact?.name || 'Unknown';
            const targetName = rel.target_artifact?.name || 'Unknown';
            relationshipList += `${sourceName} → ${targetName}<br/>`;
          });

          tooltip
            .html(
              `
            <strong>${type.replace('_', ' ')}</strong><br/>
            Count: ${typeRelationships.length}<br/>
            <hr/>
            ${relationshipList}
          `
            )
            .style('left', event.pageX + 10 + 'px')
            .style('top', event.pageY - 28 + 'px');
        }
      })
      .on('mouseout', function () {
        d3.selectAll('.tooltip').remove();
      })
      .on('click', function () {
        const type = (this as Element).getAttribute('data-relationship-type');
        if (type && onRelationshipSelect) {
          const typeRelationships =
            relationshipsByType.get(type as ArtifactStateTransitionType) || [];
          if (typeRelationships.length > 0) {
            onRelationshipSelect(typeRelationships[0].id);
          }
        }
      });

    // Add zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom as d3.ZoomBehavior<SVGSVGElement, unknown>);
  }, [relationships, onRelationshipSelect]);

  return (
    <div className="layered-view">
      <svg ref={svgRef}></svg>
    </div>
  );
};
